import { ArrowLeftOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Skeleton, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"
import { Link } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { ContractForm } from "../ContractAddPage/components/ContractForm.tsx"
import type { ContractFormData } from "../ContractAddPage/components/contract-form-data.tsx"
import {
  fetchContract,
  generateContract,
  getContractPreview,
  updateContract
} from "../redux/contracts.thunks.ts"
import type { ContractRequestData } from "../redux/contracts.thunks.types.ts"
import { ContractEditPreviewHTML } from "./components/ContractEditPreviewHTML.tsx"

function ContractEditPage({
  getContract,
  updateContract,
  generateContract,
  createContractPreview,
  contract,
  saving,
  fetching
}: ConnectedProps<typeof connector>) {
  const id = useParams()?.id as string
  const navigate = useNavigate()
  const [previewHTML, setPreviewHTML] = useState<string | null>(null)
  const [formData, setFormData] = useState<ContractRequestData | null>(null)

  const hasPDF = !!contract?.attachments?.length

  useEffect(() => {
    if (!id) return

    getContract(id)
  }, [id])

  useEffect(() => {
    generateDocumentTitle("Edit Contract")
  }, [])

  if (fetching) {
    return <Skeleton.Node active className={"size-full"} />
  }

  if (!contract) {
    return (
      <Empty
        className={"mx-auto"}
        description={"The contract was not found."}
      />
    )
  }

  const handleUpdate = async (data: Partial<ContractFormData>) => {
    const params: ContractRequestData = {
      ...data,
      // lead_id: contract?.lead_id,  // EZB-1759
      contract_id: contract?.id
    }
    setFormData(params)
    const html = await createContractPreview(params)?.unwrap()
    if (html) setPreviewHTML(html)
  }

  const handleGenerate = () => {
    if (!formData) return

    return generateContract({
      data: formData,
      navigate,
      leadId: contract?.lead_id,
      contractId: contract?.id
    })
  }

  const handleConfirm = async () => {
    if (!formData) return
    const contractId = contract?.id

    try {
      if (hasPDF) {
        await updateContract({ data: formData, contractId }).unwrap()
        await handleGenerate()
      } else {
        await updateContract({
          data: formData,
          contractId,
          showSuccessMessage: true
        }).unwrap()
        navigate(RoutesPaths.lead.view(contract.lead_id))
      }
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <div className={"grid"}>
      {previewHTML && (
        <ContractEditPreviewHTML
          html={previewHTML}
          saving={saving}
          onClickConfirm={handleConfirm}
          onClickGenerate={handleGenerate}
          onClose={() => setPreviewHTML(null)}
        />
      )}

      <Flex
        vertical
        gap={"small"}
        className={"max-h-[calc(100vh-90px)] overflow-auto"}
      >
        <Flex
          align={"center"}
          justify={"space-between"}
          className={"sticky top-0 z-10 bg-background"}
        >
          <Typography.Title level={2}>Edit Contract</Typography.Title>
          <Link to={RoutesPaths.lead.view(contract?.lead_id)}>
            <Button icon={<ArrowLeftOutlined />}>Back to Lead</Button>
          </Link>
        </Flex>
        <ContractForm
          saving={saving}
          onSubmit={handleUpdate}
          projectTypes={contract?.project_type_details}
          initialValues={
            {
              ...contract?.contract_data,
              company: {
                ...(contract?.contract_data?.company || {}),
                logo_url:
                  contract?.logo_url ||
                  contract?.contract_data?.company?.logo_url
              },
              visible_blocks: contract?.visible_blocks
            } as ContractFormData
          }
          checkDirty={!previewHTML}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  contract: state.contracts.contract,
  saving: state.contracts.saveFetching,
  fetching: state.contracts.fetching
})

const mapDispatchToProps = {
  generateContract,
  updateContract,
  getContract: fetchContract,
  createContractPreview: getContractPreview
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContractEditPage)
